import { useState, useCallback, useEffect } from 'react';
import { logger } from '@/lib/monitoring/logger';

/**
 * CSRF Token Hook
 * Provides easy-to-use CSRF token management for client components
 */

interface UseCSRFReturn {
  fetchWithCSRF: (url: string, options?: RequestInit) => Promise<Response>;
  csrfToken: string | null;
  refreshToken: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook for managing CSRF tokens and making protected requests
 */
export function useCSRF(): UseCSRFReturn {
  const [csrfToken, setCSRFToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch a new CSRF token from the server
   */
  const refreshToken: () => Promise<void> = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/csrf-token', { credentials: 'include' });
      if (!response.ok) {
        throw new Error('Failed to fetch CSRF token');
      }
      
      const data = await response.json();
      setCSRFToken(data.csrfToken);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      void logger.error('CSRF token fetch error', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Fetch with automatic CSRF token inclusion
   * Will automatically get a token if one doesn't exist
   */
  const fetchWithCSRF = useCallback(async (
    url: string, 
    options: RequestInit = {}
  ): Promise<Response> => {
    // Get token if we don't have one
    let token = csrfToken;
    if (!token) {
      setIsLoading(true);
      try {
        const tokenResponse = await fetch('/api/csrf-token', { credentials: 'include' });
        if (!tokenResponse.ok) {
          throw new Error('Failed to fetch CSRF token');
        }
        const data = await tokenResponse.json();
        token = data.csrfToken;
        setCSRFToken(token);
      } catch {
        setIsLoading(false);
        throw new Error('Failed to get CSRF token for request');
      } finally {
        setIsLoading(false);
      }
    }

    // Only add CSRF token for state-changing methods
    const method = options.method?.toUpperCase() || 'GET';
    const needsCSRF = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);
    
    const headers = new Headers(options.headers || {});
    
    if (needsCSRF && token) {
      headers.set('X-CSRF-Token', token);
    }

    // Make the actual request
    const requestInit: RequestInit = {
      ...options,
      headers,
    };

    if (!requestInit.credentials) {
      requestInit.credentials = 'include';
    }

    const response = await fetch(url, requestInit);

    // If we get a 403 with CSRF error, try refreshing token and retry once
    if (response.status === 403 && needsCSRF) {
      const data = await response.clone().json().catch(() => ({}));
      if (data.code === 'CSRF_INVALID') {
        // Token might be expired, refresh and retry
        const newTokenResponse = await fetch('/api/csrf-token', { credentials: 'include' });
        if (newTokenResponse.ok) {
          const newData = await newTokenResponse.json();
          const newToken = newData.csrfToken;
          setCSRFToken(newToken);
          
          // Retry with new token
          headers.set('X-CSRF-Token', newToken);
          const retryInit: RequestInit = {
            ...options,
            headers,
          };
          if (!retryInit.credentials) {
            retryInit.credentials = 'include';
          }
          return fetch(url, retryInit);
        }
      }
    }

    return response;
  }, [csrfToken]);

  // Optionally pre-fetch token on mount
  useEffect(() => {
    if (!csrfToken) {
      void refreshToken();
    }
  }, [csrfToken, refreshToken]);

  return {
    fetchWithCSRF,
    csrfToken,
    refreshToken,
    isLoading,
    error
  };
}

/**
 * Utility function for one-off CSRF-protected requests
 * Use this when you don't need the full hook functionality
 */
let cachedCSRFToken: string | null = null;
let pendingTokenRequest: Promise<string> | null = null;

async function requestNewCSRFToken(): Promise<string> {
  if (!pendingTokenRequest) {
    pendingTokenRequest = fetch('/api/csrf-token', { credentials: 'include' })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to fetch CSRF token');
        }
        return response.json() as Promise<{ csrfToken?: string }>;
      })
      .then(({ csrfToken }) => {
        if (!csrfToken) {
          throw new Error('CSRF token payload missing token');
        }
        cachedCSRFToken = csrfToken;
        return csrfToken;
      })
      .finally(() => {
        pendingTokenRequest = null;
      });
  }

  return pendingTokenRequest;
}

async function ensureCSRFToken(): Promise<string> {
  if (cachedCSRFToken) {
    return cachedCSRFToken;
  }
  return requestNewCSRFToken();
}

function buildRequestInit(options: RequestInit, token?: string): RequestInit {
  const headers = new Headers(options.headers || {});
  if (token) {
    headers.set('X-CSRF-Token', token);
  }

  const requestInit: RequestInit = {
    ...options,
    headers,
  };
  if (!requestInit.credentials) {
    requestInit.credentials = 'include';
  }
  return requestInit;
}

export async function fetchWithCSRFToken(url: string, options: RequestInit = {}): Promise<Response> {
  const method = options.method?.toUpperCase() || 'GET';
  const needsCSRF = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);

  if (!needsCSRF) {
    return fetch(url, buildRequestInit(options));
  }

  let token = await ensureCSRFToken();
  let response = await fetch(url, buildRequestInit(options, token));

  if (response.status === 403) {
    const body = (await response.clone().json().catch(() => null)) as { code?: string } | null;
    if (body?.code === 'CSRF_INVALID') {
      cachedCSRFToken = null;
      token = await requestNewCSRFToken();
      response = await fetch(url, buildRequestInit(options, token));
    }
  }

  return response;
}
